import { API_BASE, API } from "@/config/apiBase";
import { initializeTheme } from "@/config/theme";
import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

// Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import Dashboard from "@/pages/Dashboard";
import ContactDetail from "@/pages/ContactDetail";
import Settings from "@/pages/Settings";
import PricingPage from "@/pages/PricingPage";
import GiftShopPage from "@/pages/GiftShopPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import CalendarPage from "@/pages/CalendarPage";
import GiftTracking from "@/pages/GiftTracking";
import AdminDashboard from "@/pages/AdminDashboard";
import AnalyticsEvents from "@/pages/admin/AnalyticsEvents";
import TermsOfService from "@/pages/TermsOfService";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import AuthCallbackPage from "@/pages/AuthCallbackPage";

// Initialize theme on app load (before render)
initializeTheme();

// App version for deployment verification
const APP_VERSION = process.env.REACT_APP_VERSION || '1.0.0';
const BUILD_TIME = process.env.REACT_APP_BUILD_TIME || new Date().toISOString();

// Log version on app load (for deployment verification)
console.log(`%c🚀 OuttaTouch v${APP_VERSION}`, 'color: #E07A5F; font-weight: bold; font-size: 14px;');
console.log(`%c   Build: ${BUILD_TIME}`, 'color: #6D6F7C; font-size: 11px;');
console.log(`%c   API Base: ${API_BASE}`, 'color: #6D6F7C; font-size: 11px;');
console.log(`%c   Token in localStorage: ${localStorage.getItem("token") ? "Yes" : "No"}`, 'color: #6D6F7C; font-size: 11px;');

// Configure axios defaults
axios.defaults.withCredentials = true;

// Add axios interceptor to include auth token from localStorage
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Log PUT requests for debugging
    if (config.method === 'put') {
      console.log(`[Axios] PUT ${config.url}`, config.data);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      // Don't redirect here - let ProtectedRoute handle it
    }
    return Promise.reject(error);
  }
);

// Auth Context with logout function
export const AuthContext = React.createContext({
  user: null,
  setUser: () => {},
  logout: () => {},
  isAuthenticated: false
});

// Global logout function that can be called from anywhere
export const performLogout = (navigate) => {
  console.log("[Auth] Performing logout - clearing all state");
  
  // Clear localStorage
  localStorage.removeItem("token");
  localStorage.removeItem("user");
  
  // Clear axios default header
  delete axios.defaults.headers.common['Authorization'];
  
  // Redirect immediately with replace
  if (navigate) {
    navigate("/login", { replace: true });
  } else {
    window.location.replace("/login");
  }
};

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          const response = await axios.post(
            `${API}/auth/google/session`,
            {},
            { headers: { "X-Session-ID": sessionId } }
          );
          
          // Clear hash and navigate to dashboard with user data
          window.history.replaceState(null, "", window.location.pathname);
          navigate("/dashboard", { state: { user: response.data }, replace: true });
        } catch (error) {
          console.error("Auth error:", error);
          navigate("/login", { replace: true });
        }
      } else {
        navigate("/login", { replace: true });
      }
    };

    processAuth();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#E07A5F] border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-[#3D405B]">Signing you in...</p>
      </div>
    </div>
  );
};

const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // Synchronous check - if no token, immediately not authenticated
    return localStorage.getItem("token") ? null : false;
  });
  const [user, setUser] = useState(location.state?.user || null);
  const hasCheckedInvite = useRef(false);

  // Logout function to pass through context
  const logout = () => {
    console.log("[Auth] Logout called - clearing state immediately");
    setUser(null);
    setIsAuthenticated(false);
    performLogout(navigate);
  };

  // Check for pending family invite after authentication
  const checkPendingFamilyInvite = async () => {
    if (hasCheckedInvite.current) return;
    
    const pendingInvite = localStorage.getItem("pendingFamilyInvite");
    if (!pendingInvite) return;
    
    hasCheckedInvite.current = true;
    console.log("[Auth] Found pending family invite:", pendingInvite);
    
    try {
      const response = await axios.post(`${API}/family/invite/${pendingInvite}/accept`);
      console.log("[Auth] Family invite accepted:", response.data);
      localStorage.removeItem("pendingFamilyInvite");
      
      // Refresh user data to get updated family info
      const userResponse = await axios.get(`${API}/auth/me`);
      setUser(userResponse.data);
      
      // Redirect to family settings
      navigate("/settings?tab=family", { replace: true });
    } catch (error) {
      console.error("[Auth] Failed to accept family invite:", error);
      localStorage.removeItem("pendingFamilyInvite");
      // Don't block the user - they can try again manually
    }
  };

  useEffect(() => {
    // If already determined not authenticated, don't check again
    if (isAuthenticated === false) {
      // Before redirecting, preserve any invite token from URL
      const urlParams = new URLSearchParams(location.search);
      const inviteId = urlParams.get("invite");
      if (inviteId) {
        console.log("[Auth] Preserving family invite before login redirect:", inviteId);
        localStorage.setItem("pendingFamilyInvite", inviteId);
      }
      navigate("/login", { replace: true });
      return;
    }

    const checkAuth = async () => {
      // First check if token exists
      const token = localStorage.getItem("token");
      if (!token) {
        console.log("[ProtectedRoute] No token found - redirecting to login");
        // Preserve invite token before redirect
        const urlParams = new URLSearchParams(location.search);
        const inviteId = urlParams.get("invite");
        if (inviteId) {
          console.log("[Auth] Preserving family invite before login redirect:", inviteId);
          localStorage.setItem("pendingFamilyInvite", inviteId);
        }
        setIsAuthenticated(false);
        navigate("/login", { replace: true });
        return;
      }

      // If we have user from navigation state, use it but still verify
      if (location.state?.user) {
        setUser(location.state.user);
      }

      // Always verify token with backend
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
        setIsAuthenticated(true);
        console.log("[ProtectedRoute] Auth verified for:", response.data.email);
        
        // After successful auth, check for pending family invite
        await checkPendingFamilyInvite();
      } catch (error) {
        console.log("[ProtectedRoute] Auth check failed - clearing token");
        // Token invalid - clear it and redirect
        performLogout(navigate);
        setIsAuthenticated(false);
      }
    };

    checkAuth();
  }, [location.pathname, navigate, isAuthenticated]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#E07A5F] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <AuthContext.Provider value={{ user, setUser, logout, isAuthenticated }}>
      {children}
    </AuthContext.Provider>
  );
};

const AppRouter = () => {
  const location = useLocation();

  // Check for session_id in hash FIRST (before any other route matching)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contact/:contactId"
        element={
          <ProtectedRoute>
            <ContactDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/gifts"
        element={
          <ProtectedRoute>
            <GiftShopPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/calendar"
        element={
          <ProtectedRoute>
            <CalendarPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/gifts/tracking"
        element={
          <ProtectedRoute>
            <GiftTracking />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/analytics/events"
        element={
          <ProtectedRoute>
            <AnalyticsEvents />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment/success"
        element={
          <ProtectedRoute>
            <PaymentSuccessPage />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

// Need to import React for createContext
import React from "react";

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
        <Toaster position="top-right" richColors closeButton />
      </BrowserRouter>
    </div>
  );
}

export default App;
